import React, { useState, useEffect, useContext } from 'react'
import { Modal, Switch, Table, Tag, Button, Alert, Spin } from 'antd'
import {
  EyeOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PrinterOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import client from 'apollo'
import openNotification from 'utils/Notification'
import { AppContext } from 'AppContext'
import { GET_USER_ACTION_PERMISSIONS } from '../graphql/Queries'
import { UPDATE_USER_ACTION_PERMISSIONS } from '../graphql/Mutations'

const defaultAuditPermissions = {
  canView: true,
  canAdd: false,
  canEdit: false,
  canDelete: false,
  canDownload: false,
  canPrint: false,
  canExport: false,
  canSave: false,
}

const defaultAdminPermissions = {
  canView: true,
  canAdd: true,
  canEdit: true,
  canDelete: true,
  canDownload: true,
  canPrint: true,
  canExport: true,
  canSave: true,
}

const UserPermissionModal = ({
  visible,
  onClose,
  userId,
  userName,
  userRole,
  draftPermissions,
  onSaveDraft,
}) => {
  const { state: { currentUser } = {}, dispatch } = useContext(AppContext) || {}
  const isAudit = userRole === 'AUDIT'
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [permissions, setPermissions] = useState(
    draftPermissions || (isAudit ? { ...defaultAuditPermissions } : { ...defaultAdminPermissions })
  )

  useEffect(() => {
    if (!visible) return

    if (userId) {
      setLoading(true)
      client
        .query({
          query: GET_USER_ACTION_PERMISSIONS,
          variables: { userId: String(userId) },
          fetchPolicy: 'network-only',
        })
        .then((res) => {
          const data = res?.data?.getUserActionPermissions
          if (data) {
            const loaded = {
              canView: !!data.canView,
              canAdd: !!data.canAdd,
              canEdit: !!data.canEdit,
              canDelete: !!data.canDelete,
              canDownload: !!data.canDownload,
              canPrint: !!data.canPrint,
              canExport: !!data.canExport,
              canSave: !!data.canSave,
            }
            setPermissions(loaded)
            if (onSaveDraft) {
              onSaveDraft(loaded)
            }
          } else {
            const fallback = isAudit
              ? { ...defaultAuditPermissions }
              : { ...defaultAdminPermissions }
            setPermissions(fallback)
            if (onSaveDraft) {
              onSaveDraft(fallback)
            }
          }
        })
        .catch((err) => {
          console.error('Error fetching user action permissions:', err)
          const fallback = isAudit
            ? { ...defaultAuditPermissions }
            : { ...defaultAdminPermissions }
          setPermissions(fallback)
        })
        .finally(() => setLoading(false))
    } else {
      if (draftPermissions) {
        setPermissions({ ...draftPermissions })
      } else {
        const fallback = isAudit
          ? { ...defaultAuditPermissions }
          : { ...defaultAdminPermissions }
        setPermissions(fallback)
        if (onSaveDraft) {
          onSaveDraft(fallback)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, userId])

  const handleToggle = (key, value) => {
    setPermissions((prev) => {
      const next = {
        ...prev,
        [key]: value,
      }
      if (onSaveDraft) {
        onSaveDraft(next)
      }
      return next
    })
  }

  const handleSave = () => {
    if (onSaveDraft) {
      onSaveDraft({ ...permissions })
    }

    if (!userId) {
      openNotification('success', 'User permissions configured for new user')
      onClose()
      return
    }

    setSaving(true)
    const payload = {
      canView: !!permissions.canView,
      canAdd: !!permissions.canAdd,
      canEdit: !!permissions.canEdit,
      canDelete: !!permissions.canDelete,
      canDownload: !!permissions.canDownload,
      canPrint: !!permissions.canPrint,
      canExport: !!permissions.canExport,
      canSave: !!permissions.canSave,
    }

    client
      .mutate({
        mutation: UPDATE_USER_ACTION_PERMISSIONS,
        variables: {
          userId: String(userId),
          data: payload,
        },
      })
      .then(() => {
        openNotification('success', 'User permissions updated successfully')
        if (currentUser && String(currentUser.id) === String(userId)) {
          if (typeof window !== 'undefined') {
            window.__CURRENT_USER_PERMISSIONS__ = payload
            try {
              sessionStorage.setItem(
                'finideas_user_permissions',
                JSON.stringify(payload)
              )
            } catch (e) {}
          }
          if (dispatch) {
            dispatch({
              type: 'USER_ACTION_PERMISSIONS',
              data: payload,
            })
            dispatch({
              type: 'CURRENT_USER',
              data: {
                ...currentUser,
                permissions: payload,
              },
            })
          }
        }
        onClose()
      })
      .catch((err) => {
        console.error('Error saving user action permissions:', err)
        openNotification('error', err.message || 'Failed to update permissions')
      })
      .finally(() => setSaving(false))
  }

  const permissionItems = [
    {
      key: 'canView',
      title: 'View / Read',
      icon: <EyeOutlined style={{ color: '#1890ff', fontSize: 16 }} />,
      description: 'Allow viewing records and reports',
      enabled: permissions.canView,
      disabled: false,
    },
    {
      key: 'canAdd',
      title: 'Add',
      icon: <PlusCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />,
      description: 'Create new entries and records',
      enabled: permissions.canAdd,
      disabled: false,
    },
    {
      key: 'canEdit',
      title: 'Edit / Update',
      icon: <EditOutlined style={{ color: '#fa8c16', fontSize: 16 }} />,
      description: 'Modify existing entries and records',
      enabled: permissions.canEdit,
      disabled: false,
    },
    {
      key: 'canDelete',
      title: 'Delete',
      icon: <DeleteOutlined style={{ color: '#f5222d', fontSize: 16 }} />,
      description: 'Delete or remove entries and records',
      enabled: permissions.canDelete,
      disabled: false,
    },
    {
      key: 'canDownload',
      title: 'Download',
      icon: <DownloadOutlined style={{ color: '#722ed1', fontSize: 16 }} />,
      description: 'Download reports, statements, or documents',
      enabled: permissions.canDownload,
      disabled: false,
    },
    {
      key: 'canPrint',
      title: 'Print',
      icon: <PrinterOutlined style={{ color: '#13c2c2', fontSize: 16 }} />,
      description: 'Print reports, statements, or screens',
      enabled: permissions.canPrint,
      disabled: false,
    },
    {
      key: 'canExport',
      title: 'Export',
      icon: <ExportOutlined style={{ color: '#eb2f96', fontSize: 16 }} />,
      description: 'Export data to Excel, CSV, or external formats',
      enabled: permissions.canExport,
      disabled: false,
    },
    {
      key: 'canSave',
      title: 'Save / Submit',
      icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />,
      description: 'Submit forms and commit transactional updates',
      enabled: permissions.canSave,
      disabled: false,
    },
  ]

  const columns = [
    {
      title: 'Permission',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.icon}
          <div>
            <div style={{ fontWeight: 600, color: '#262626' }}>{text}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {record.description}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'status',
      width: 100,
      render: (enabled) => (
        <Tag
          color={enabled ? 'green' : 'default'}
          style={{ minWidth: 46, textAlign: 'center', fontWeight: 600 }}
        >
          {enabled ? 'ON' : 'OFF'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'enabled',
      key: 'action',
      width: 100,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          disabled={record.disabled}
          onChange={(val) => handleToggle(record.key, val)}
        />
      ),
    },
  ]

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SafetyCertificateOutlined style={{ color: '#b32025', fontSize: 20 }} />
          <span>
            User Permissions
            {userName && userName !== '-' ? ` - ${userName}` : (userId ? '' : ' - (New User)')}
            {userRole ? ` (${userRole})` : ''}
          </span>
        </div>
      }
      visible={visible}
      onCancel={() => {
        if (onSaveDraft) {
          onSaveDraft({ ...permissions })
        }
        onClose()
      }}
      width={680}
      centered
      bodyStyle={{
        maxHeight: 'calc(80vh - 160px)',
        overflowY: 'auto',
        padding: '16px 24px',
      }}
      footer={[
        <Button
          key='cancel'
          onClick={() => {
            if (onSaveDraft) {
              onSaveDraft({ ...permissions })
            }
            onClose()
          }}
        >
          Close
        </Button>,
        <Button
          key='save'
          type='primary'
          loading={saving}
          onClick={handleSave}
          style={{
            backgroundColor: '#b32025',
            borderColor: '#b32025',
          }}
        >
          Save Permissions
        </Button>,
      ]}
    >
      {isAudit && (
        <Alert
          type='info'
          showIcon
          message='Audit Role Permissions'
          description='Default permissions for Audit are View / Read ON and other actions OFF. You can toggle any permission ON or OFF as required.'
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size='large' />
        </div>
      ) : (
        <Table
          dataSource={permissionItems}
          columns={columns}
          pagination={false}
          size='middle'
          rowKey='key'
        />
      )}
    </Modal>
  )
}

export default UserPermissionModal
